/**
 * Time utilities for Toronto timezone handling
 * Issue #3 FIX: All date calculations use Toronto timezone for consistency with backend
 */

/**
 * Get today's date in Toronto timezone as YYYY-MM-DD string
 * @returns {string} Today's date in YYYY-MM-DD format (Toronto timezone)
 */
export const todayToronto = () => {
  // Issue #3 FIX: Use Toronto timezone instead of browser local time
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
};

/**
 * Get the first day of the current month in Toronto timezone as YYYY-MM-DD string
 * @returns {string} First day of current month in YYYY-MM-DD format (Toronto timezone)
 */
export const startOfCurrentMonthToronto = () => {
  const torontoDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  return torontoDate.slice(0, 7) + '-01';
};

/**
 * Get a date N days ago in Toronto timezone as YYYY-MM-DD string
 * @param {number} days - Number of days to subtract
 * @returns {string} Date in YYYY-MM-DD format (Toronto timezone)
 */
export const daysAgoToronto = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
};

/**
 * Get a date N months ago in Toronto timezone as YYYY-MM-DD string
 * @param {number} months - Number of months to subtract
 * @returns {string} Date in YYYY-MM-DD format (Toronto timezone)
 */
export const monthsAgoToronto = (months) => {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
};

/**
 * Get a date N years ago in Toronto timezone as YYYY-MM-DD string
 * @param {number} years - Number of years to subtract
 * @returns {string} Date in YYYY-MM-DD format (Toronto timezone)
 */
export const yearsAgoToronto = (years) => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - years);
  return date.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
};

/**
 * Calculate start date based on range name using Toronto timezone
 * @param {string} range - One of: 'today', 'week', 'month', '6months', 'year'
 * @returns {string} Start date in YYYY-MM-DD format (Toronto timezone)
 */
export const getStartDateForRange = (range) => {
  switch (range) {
    case 'week':
      return daysAgoToronto(7);
    case 'month':
      return monthsAgoToronto(1);
    case '6months':
      return monthsAgoToronto(6);
    case 'year':
      return yearsAgoToronto(1);
    case 'today':
    default:
      return todayToronto();
  }
};

/**
 * Get the next service day (Mon-Fri) in Toronto timezone
 * Uses UTC-only arithmetic to avoid browser-timezone bugs (e.g. IST users seeing wrong day)
 * @param {string} dateStr - Optional start date string (YYYY-MM-DD)
 * @returns {string} Next service day in YYYY-MM-DD format
 */
export const nextServiceDayToronto = (dateStr = null) => {
  // Get Toronto's current date as a clean YYYY-MM-DD string
  const todayStr = dateStr || new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  // Parse as UTC noon — no timezone offset can shift noon to a different calendar date
  const date = new Date(todayStr + 'T12:00:00Z');

  // Always start from tomorrow (current date + 1)
  date.setUTCDate(date.getUTCDate() + 1);

  let day = date.getUTCDay();

  // If Saturday (6), add 2 days to get to Monday
  if (day === 6) date.setUTCDate(date.getUTCDate() + 2);
  // If Sunday (0), add 1 day to get to Monday
  else if (day === 0) date.setUTCDate(date.getUTCDate() + 1);

  return date.toISOString().slice(0, 10);
};

/**
 * Get tomorrow's date in Toronto timezone as YYYY-MM-DD string
 * Uses UTC-only arithmetic to avoid browser-timezone bugs
 * @returns {string} Tomorrow's date in YYYY-MM-DD format (Toronto timezone)
 */
export const tomorrowToronto = () => {
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  const date = new Date(todayStr + 'T12:00:00Z');
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};

/**
 * Get the earliest selectable weekday start date (tomorrow or later, skipping weekends)
 * Used as the min value for signup date pickers so weekends are never the minimum.
 * Uses UTC-only arithmetic to avoid browser-timezone bugs
 * @returns {string} Next weekday from tomorrow in YYYY-MM-DD format (Toronto timezone)
 */
export const minStartDateToronto = () => {
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  const date = new Date(todayStr + 'T12:00:00Z');
  date.setUTCDate(date.getUTCDate() + 1);
  let day = date.getUTCDay();
  if (day === 6) date.setUTCDate(date.getUTCDate() + 2);
  else if (day === 0) date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};

/**
 * For active customers renewing before end date:
 * returns the next weekday AFTER endDate (YYYY-MM-DD).
 * Falls back to minStartDateToronto() if endDate is missing or in the past.
 * @param {string} endDateStr - Current plan end date as YYYY-MM-DD
 * @returns {string} Minimum renewal start date in YYYY-MM-DD format
 */
export const renewalMinStartDate = (endDateStr) => {
  if (!endDateStr) return minStartDateToronto();

  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  if (endDateStr <= todayStr) return minStartDateToronto();

  // Day after end date
  const date = new Date(endDateStr + 'T12:00:00Z');
  date.setUTCDate(date.getUTCDate() + 1);
  let dow = date.getUTCDay();
  if (dow === 6) date.setUTCDate(date.getUTCDate() + 2);
  else if (dow === 0) date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};
